import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { useDocuments } from 'lib/hooks/queries/documents/useDocuments';

import AGTable from 'components/shared/AGTable/AGTable';
import { clinicalNote } from 'components/state';

import { CustomStatusBarCount } from '../AGTable/CustomStatusBarCount';
import { RowSelectionActions } from '../AgTablePage/components/RowSelectionActions';
import TableFilters from '../AgTablePage/components/TableFilters';
import CustomPagination from '../CustomPagination/CustomPagination';

import DocumentPracticeShare from './components/DocumentPracticeShare';
import DocumentsHeader from './components/DocumentsHeader';
import SelectedDocumentsActions from './components/SelectedDocumentsActions';
import formatFilters from './lib/formatFilters';

const List = ({
  setViewDocument,
  setShowInstantPacket,
  setPatientSelection,
  categories,
  patientId,
  sendFaxModal,
  setSendFaxModal,
  options,
  level,
  appointmentId,
  shareWithPracticeModal,
  setShareWithPracticeModal
}) => {
  const { locked: isLocked } = useRecoilValue(clinicalNote);

  const { myPortalPractice } = useParams();

  const {
    filters,
    page,
    setPage,
    limit,
    sort,
    setFilters,
    isEditingFilter,
    setIsEditingFilter,
    defaultFilters,
    initialFilters
  } = useTableContext();

  const { data, isLoading } =
    useDocuments({
      level,
      params: formatFilters({
        filters,
        page,
        limit,
        sort,
        patientId,
        myPortalPractice,
        appointmentId,
        level
      }),
      dependencies: [
        ...(level === 'chart' ? [patientId] : level === 'attachment' ? [appointmentId] : []),
        formatFilters({
          filters,
          page,
          limit,
          sort,
          patientId,
          myPortalPractice,
          appointmentId: appointmentId ? appointmentId?.toString() : null,
          level
        })
      ]
    }) || {};

  const documents = data?.documents || [];
  const totalDocuments = data?.totalDocuments || 0;

  const onPageChange = useCallback((currentPage) => setPage(currentPage), [setPage]);

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: CustomStatusBarCount,
          statusPanelParams: {
            data: documents,
            count: totalDocuments,
            page,
            limit
          },
          align: 'left'
        },
        {
          statusPanel: CustomPagination,
          statusPanelParams: {
            containerClassName: 'flex',
            onPageChange,
            perPage: limit,
            totalItems: totalDocuments,
            page
          },
          align: 'right'
        }
      ]
    }),
    [page, limit, setPage, totalDocuments]
  );

  const onRowClicked = (event) => {
    const { data } = event;

    if (!event.event.target.closest('.ag-selection-checkbox')) {
      setViewDocument({
        ...data,
        load: true,
        title: data?.name,
        handle: data?.document_handle || {
          response_id: data?.id,
          appointment_id: data?.appointment_id,
          form_id: data?.form_id,
          json: data?.json,
          form_json: data?.form_json,
          form_version: data?.form_version
        },
        category: categories.find((cat) => cat?.value == data?.category?.trim()),
        type: data.kind === 'fax' ? 'fax' : data?.mimetype || 'form',
        id: data?.id,
        patient_id: data?.patient_id,
        uploader_id: data?.uploader_id,
        appointment_id: data?.appointment_id
      });
    }
  };

  const onCellClicked = async (e) => {
    if (['actions', '0', 'tagLinks'].includes(e?.column?.colId)) return;
    await onRowClicked(e);
  };

  return (
    <>
      <DocumentsHeader
        setViewDocument={setViewDocument}
        setShowInstantPacket={setShowInstantPacket}
        options={options}
        patientId={patientId}
        appointmentId={appointmentId}
        isLocked={isLocked}
        level={level}
        setPatientSelection={setPatientSelection}
      />
      <TableFilters
        category="documents"
        filters={filters}
        setFilters={setFilters}
        initialFilters={initialFilters}
        defaultFilters={defaultFilters}
        isEditingFilter={isEditingFilter}
        setIsEditingFilter={setIsEditingFilter}
      />
      <div className="flex h-full flex-col overflow-hidden">
        <div className="ag-theme-quartz h-full">
          <AGTable
            data={documents}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            statusBar={statusBar}
            customClassName="ag-grid-interactive"
            loading={isLoading || !categories}
            masterDetail={true}
            onCellClicked={onCellClicked}
          />
          <RowSelectionActions
            actions={({ selectedRows, setSelectedRows, gridApi }) => (
              <SelectedDocumentsActions
                sendFaxModal={sendFaxModal}
                options={options}
                setSendFaxModal={setSendFaxModal}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                gridApi={gridApi}
              />
            )}
          />
        </div>
      </div>

      {shareWithPracticeModal?.show && (
        <DocumentPracticeShare
          selectedFiles={shareWithPracticeModal?.files}
          handleOpen={shareWithPracticeModal?.show}
          handleClose={() => setShareWithPracticeModal({ show: false, files: [] })}
        />
      )}
    </>
  );
};

export default List;
